import { sitApi } from './sitApiClient';
import { callQualityJudge } from '../../../shared/services/qualityJudgeService';

/**
 * Convert a File (from <FileInput> / drop) to base64 (no data: prefix).
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * POST /imports — one OR MORE diagrams (tree) → SET of SIT test plans.
 *
 * `diagrams`: array of { id, label, file, parentDiagramId?, parentBranch? }.
 * Exactly one entry must have no parentDiagramId (the root). Every other entry
 * must reference an existing parent and supply a parentBranch hint
 * (e.g. "press 1", "default transfer").
 *
 * Returns: { plans: TestPlan[], modelId, retries, warnings: string[] }.
 */
export async function importDiagrams({
  diagrams,
  notes,
  maxPlans,
  ivrStyle,
  expectPromptBaseTimeoutSec,
  priorJudgeFindings
}) {
  const encoded = await Promise.all(
    diagrams.map(async (d) => ({
      id: d.id,
      label: d.label,
      imageBase64: await fileToBase64(d.file),
      mediaType: d.file.type || 'image/png',
      parentDiagramId: d.parentDiagramId ?? null,
      parentBranch: d.parentBranch ?? null
    }))
  );
  const body = { diagrams: encoded };
  if (notes) body.notes = notes;
  if (maxPlans) body.maxPlans = maxPlans;
  if (ivrStyle && ivrStyle !== 'hybrid') body.ivrStyle = ivrStyle;
  if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
    body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }
  const { data } = await sitApi.post('/imports', body);
  return data;
}

/**
 * Legacy single-diagram wrapper for callers that haven't migrated.
 * Prefer importDiagrams() for new code.
 */
export async function importDiagram(file, notes, maxPlans) {
  return importDiagrams({
    diagrams: [{ id: 'root', label: 'Root diagram', file }],
    notes,
    maxPlans
  });
}

/**
 * POST /imports kind=kb — KB artifacts → conversational AI-agent test plans.
 *
 * Each entry in `artifacts` is { id, label, file?, text? }:
 *   - file (.pdf): base64-encoded, sent as `kind: "pdf"` to Bedrock.
 *   - text (md/html/plain): sent inline as `kind: "text"`.
 *
 * `inlineText` is an additional engineer-pasted KB body (Markdown / plain
 * text). Either artifacts OR inlineText is required.
 *
 * `testMix` controls how many of each kind of test to emit:
 *   { positivePerTopic?: 1, includeNegative?: true, includePersona?: true }
 *
 * Returns: { plans: TestPlan[], modelId, retries, warnings, kbArtifacts }.
 */
export async function importKb({
  artifacts,
  inlineText,
  notes,
  maxPlans,
  testMix,
  targetDefaults,
  expectPromptBaseTimeoutSec,
  priorJudgeFindings
}) {
  const encoded = await Promise.all(
    (artifacts || []).map(async (a) => {
      if (a.file) {
        const isPdf = (a.file.type || '').includes('pdf');
        return {
          id: a.id,
          label: a.label,
          kind: isPdf ? 'pdf' : 'text',
          body: isPdf ? await fileToBase64(a.file) : await fileToText(a.file),
          mediaType: a.file.type || (isPdf ? 'application/pdf' : 'text/plain')
        };
      }
      return {
        id: a.id,
        label: a.label,
        kind: 'text',
        body: a.text,
        mediaType: a.mediaType || 'text/markdown'
      };
    })
  );
  const body = { kind: 'kb', artifacts: encoded };
  if (inlineText) body.inlineText = inlineText;
  if (notes) body.notes = notes;
  if (maxPlans) body.maxPlans = maxPlans;
  if (testMix) body.testMix = testMix;
  if (targetDefaults) body.targetDefaults = targetDefaults;
  if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
    body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }
  const { data } = await sitApi.post('/imports', body);
  return data;
}

// File → UTF-8 text (for .md / .html / .txt KB artifacts).
function fileToText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

/**
 * POST /imports — Excel test-script spreadsheet → SET of SIT test plans.
 *
 * Backend reuses the existing `/imports` handler with a `kind: "excel"`
 * discriminator: deterministic client-side parsing groups rows into test
 * cases, then Bedrock (Sonnet 4.6) synthesizes each group's free-form
 * `callerExperience` text into typed steps (expectPrompt / pressDtmf /
 * saySpeech / expectTransfer / hangup). Response shape matches
 * importDiagrams().
 *
 * `groups`: [{ testNo, section?, expectedResults?, preconditions?,
 *              rows: [{ sequence, callerExperience, agentExperience? }] }]
 * `targetDefaults`: { ccaas?, destination?, fromDid? } — applied to every
 *   synthesized plan's `target` block.
 * `source`: { filename?, sheetName? } — preserved for warnings/debugging.
 *
 * Returns: { plans: TestPlan[], modelId, retries?, warnings?: string[] }.
 */
/**
 * API Gateway HTTP API enforces a 30s integration timeout (hard ceiling — not
 * configurable beyond that). Bedrock-Haiku output latency varies materially.
 *
 * Sizing history:
 * - 15-plan chunks land in 24–25s on a good day, but observed 14-plan chunks
 *   at 33s when the model takes a slow path → 504 from the gateway even
 *   though the Lambda completes.
 * - 10-plan chunks land in ~17s typical, but a schema-retry inside the
 *   importer Lambda (Bedrock emits e.g. an unknown property on a step) adds
 *   ~17s → ~34s total → 503. Observed in prod 2026-06-16.
 * - The importer Lambda now bails on retry the moment it has ≥1 valid plan
 *   (MIN_VALID_TO_SKIP_RETRY in backend/lambdas/importer/src/handlers.ts),
 *   so this is mostly belt-and-suspenders, but 8-plan chunks land at ~14s
 *   even on Bedrock's worst day and give margin if the bailout is bypassed
 *   for some reason (e.g., zero plans valid on first attempt → retry fires).
 *
 * Trade-off: 8 vs 10 means 25% more sequential round-trips on large
 * spreadsheets. Acceptable to eliminate 503s.
 */
const EXCEL_CHUNK_SIZE = 8;

export async function importExcel(
  {
    groups,
    targetDefaults,
    source,
    notes,
    maxPlans,
    matcherPreference,
    expectPromptBaseTimeoutSec,
    priorJudgeFindings
  },
  { onChunkProgress } = {}
) {
  const targetCount = Math.min(
    typeof maxPlans === 'number' ? maxPlans : groups.length,
    groups.length
  );
  const usable = groups.slice(0, targetCount);

  if (usable.length <= EXCEL_CHUNK_SIZE) {
    const body = {
      kind: 'excel',
      groups: usable,
      targetDefaults: targetDefaults ?? {},
      source: source ?? {}
    };
    if (notes) body.notes = notes;
    if (maxPlans) body.maxPlans = maxPlans;
    if (matcherPreference && matcherPreference !== 'auto') body.matcherPreference = matcherPreference;
    if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
      body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
    }
    if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
      body.priorJudgeFindings = priorJudgeFindings;
    }
    onChunkProgress?.({ chunk: 1, totalChunks: 1, plansSoFar: 0, totalPlans: usable.length });
    const { data } = await sitApi.post('/imports', body);
    onChunkProgress?.({
      chunk: 1,
      totalChunks: 1,
      plansSoFar: data.plans?.length ?? 0,
      totalPlans: usable.length
    });
    return data;
  }

  const chunks = [];
  for (let i = 0; i < usable.length; i += EXCEL_CHUNK_SIZE) {
    chunks.push(usable.slice(i, i + EXCEL_CHUNK_SIZE));
  }

  const plans = [];
  const warnings = [];
  let modelId;
  let retries = 0;

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    onChunkProgress?.({
      chunk: i + 1,
      totalChunks: chunks.length,
      plansSoFar: plans.length,
      totalPlans: usable.length
    });
    const body = {
      kind: 'excel',
      groups: chunk,
      targetDefaults: targetDefaults ?? {},
      source: source ?? {},
      maxPlans: chunk.length
    };
    if (notes) body.notes = notes;
    if (matcherPreference && matcherPreference !== 'auto') body.matcherPreference = matcherPreference;
    if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
      body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
    }
    if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
      body.priorJudgeFindings = priorJudgeFindings;
    }
    const { data } = await sitApi.post('/imports', body);
    if (data.plans) plans.push(...data.plans);
    if (data.warnings) warnings.push(...data.warnings);
    if (typeof data.retries === 'number') retries = Math.max(retries, data.retries);
    modelId = data.modelId || modelId;
    onChunkProgress?.({
      chunk: i + 1,
      totalChunks: chunks.length,
      plansSoFar: plans.length,
      totalPlans: usable.length
    });
  }

  return { plans, modelId, retries, warnings };
}

/**
 * Async, advisory quality grade for a set of plans.
 *
 * Fired by every importer in parallel right after `/imports` returns, so the
 * result panel can render plans immediately and update the quality badge
 * 15-30s later when the judge finishes. Hits the direct `/quality-judge` route
 * (no importer Lambda hop, so the 30s API GW timeout has more headroom for
 * Sonnet's response).
 *
 * sourceContent caveat for diagrams: the SPA only has the file labels, parent
 * branches, and engineer notes — not the image bytes. The judge can still
 * grade structural concerns (terminality, matcher-type fit, brand-hallucination,
 * obvious completeness gaps) but deep faithfulness ("does the plan match the
 * actual menu choices on screen?") is out of reach without re-uploading the
 * image. Documented in source.description so the judge knows what it's
 * working with.
 *
 * Returns: { overallScore: number, perItem: [{itemId, score, severity, reason,
 *           suggestion}], modelId, durationMs }
 */
export async function judgeImport({ kind, source, sourceContent, plans }) {
  return callQualityJudge({ kind, source, sourceContent, plans });
}
